import { memo, useCallback, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  IconButton,
  Skeleton,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import type { SxProps, Theme } from '@mui/material/styles';
import type { Provider, ProviderAssignment, ProviderRole } from '../types';
import {
  getProviderRoles,
  getBillingModes,
  type RoleDefinition,
  type BillingMode,
} from '../constants/providerConfig';
import { isCrnaCredential } from '../utils/isCrnaCredential';
import InlineAiSuggestion from './InlineAiSuggestion';

/** An OCR-derived provider suggestion for one role slot (feature 036, US4). */
export interface ClaimProviderSuggestion {
  providerId: string;
  providerName: string;
  role: ProviderRole;
  source?: {
    documentId: string;
    page: number;
    bbox: { x: number; y: number; width: number; height: number };
  };
}

/** Icons for each provider role per Figma — small (20px), gray. */
const ROLE_ICONS: Partial<Record<ProviderRole, ReactNode>> = {
  PRIMARY_SURGEON: <MedicalInformationOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  ASSISTANT_SURGEON: <MedicalServicesOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  PHYSICIANS_ASSISTANT: <LocalHospitalOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  CO_SURGEON_1: <GroupsOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  CO_SURGEON_2: <GroupsOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  ANESTHESIOLOGIST: <MedicalServicesOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  CRNA: <SupervisorAccountOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
  DEFAULT: <PersonOutlineOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />,
};

// Autocomplete/field sizing for the provider inputs — 36px to match the other inputs.
const inputStyles = {
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    bgcolor: 'background.paper',
    minHeight: 36,
    fontSize: 14,
    borderRadius: 1,
    px: 0,
  },
  // Pad the input element itself so the placeholder + typed text get the same
  // 14px inner padding as the other form inputs.
  '& .MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
    py: '7px !important',
    pl: '14px !important',
    pr: '8px !important',
    fontSize: 12,
  },
};

const chipStyles = {
  width: '100%',
  height: 36,
  borderRadius: 1,
  justifyContent: 'space-between',
  bgcolor: 'background.paper',
  borderColor: 'divider',
  color: 'text.primary',
  fontSize: 14,
  '& .MuiChip-label': {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pl: '14px',
    pr: 1,
  },
  '& .MuiChip-deleteIcon': { color: 'text.secondary' },
};

export interface ClaimTypeAssignment {
  type: string;
  providers: ProviderAssignment[];
}

interface ClaimTypeProviderPanelProps {
  /** Claim type code (e.g. 'SURGICAL', 'OFFICE_VISIT'). */
  claimType: string;
  /** Current assignment entry for this claim type, or undefined if none yet. */
  assignment: ClaimTypeAssignment | undefined;
  providers: Provider[];
  /**
   * Bubbles a new assignment up. Called with `null` when the last provider is
   * cleared (parent should remove the entry from `claimTypes`). Only ever called in
   * response to a user action — rendering an assignment never modifies it.
   */
  onAssignmentChange: (next: ClaimTypeAssignment | null) => void;
  loading: boolean;
  error: string | null;
  /** Per-claim-type validation error (e.g., surgeon/co-surgeon role conflict). */
  validationError?: string | null;
  /** OCR-derived provider suggestions for this claim type's role slots. */
  suggestions?: ClaimProviderSuggestion[];
  /** Switches the document viewer to a suggestion's source region. */
  onLocate?: (documentId: string, page: number, bbox: NonNullable<ClaimProviderSuggestion['source']>['bbox'], label: string) => void;
  /**
   * Optional override for each role label's typography. Defaults to the portal's
   * 14px/text.primary heading; the backoffice case section passes a smaller variant
   * so role labels line up with the surrounding FieldLabels.
   */
  labelSx?: SxProps<Theme>;
  /**
   * Read-only mode (e.g. claim locked from editing). Disables provider selection,
   * removal, and the billing-mode toggle — but not the AI-suggestion locate control,
   * which only navigates the document viewer.
   */
  locked?: boolean;
}

function toAssignment(provider: Provider, role: ProviderRole): ProviderAssignment {
  return {
    providerId: provider.id,
    providerName: provider.name,
    role,
    ...(provider.credential ? { credential: provider.credential } : {}),
    ...(provider.npi ? { npi: provider.npi } : {}),
  };
}

/**
 * Sets a single-assignment role's provider, preserving the assignment's position in the
 * array. The previous implementation filtered-then-appended, which moved the edited role
 * to the end — invisible while every role held one provider, but a visible reordering now
 * that a role can hold several (feature 044, FR-011).
 */
function setSingleAssignment(
  current: ProviderAssignment[],
  role: ProviderRole,
  next: ProviderAssignment | null,
): ProviderAssignment[] {
  const index = current.findIndex((a) => a.role === role);
  if (index === -1) return next ? [...current, next] : current;
  if (!next) return current.filter((_, i) => i !== index);
  return current.map((a, i) => (i === index ? next : a));
}

/** True when a provider may be picked for a role, by credential (feature 044, FR-002). */
function isEligibleForRole(provider: Provider, roleDef: RoleDefinition): boolean {
  if (roleDef.eligibility === 'crna') return isCrnaCredential(provider.credential);
  if (roleDef.eligibility === 'nonCrna') return !isCrnaCredential(provider.credential);
  return true;
}

interface RoleTileShellProps {
  roleDef: RoleDefinition;
  children: ReactNode;
  /** Optional typography override for the role label (see panel's labelSx prop). */
  labelSx?: SxProps<Theme>;
}

/** Role label (title) + the provider input — no card, just a stacked group. */
function RoleTileShell({ roleDef, children, labelSx }: RoleTileShellProps) {
  return (
    <Box
      sx={{
        ...inputStyles,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', height: 24 }}>
          {ROLE_ICONS[roleDef.code] ?? <MedicalServicesOutlinedIcon sx={{ fontSize: 20 }} />}
        </Box>
        <Typography
          sx={[
            { fontSize: 14, fontWeight: 500, color: 'text.primary', lineHeight: '24px' },
            ...(Array.isArray(labelSx) ? labelSx : [labelSx]),
          ]}
        >
          {roleDef.label}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

const ClaimTypeProviderPanel = memo(function ClaimTypeProviderPanel({
  claimType,
  assignment,
  providers,
  onAssignmentChange,
  loading,
  error,
  validationError,
  suggestions,
  onLocate,
  labelSx,
  locked,
}: ClaimTypeProviderPanelProps) {
  const roles = getProviderRoles(claimType);
  const entryProviders = assignment?.providers ?? [];

  const emitProviders = useCallback(
    (nextProviders: ProviderAssignment[]) => {
      if (nextProviders.length === 0) {
        onAssignmentChange(null);
        return;
      }
      onAssignmentChange({ type: claimType, providers: nextProviders });
    },
    [claimType, onAssignmentChange],
  );

  /** Sets (or clears) the provider on a single-assignment role. */
  const updateRole = useCallback(
    (role: ProviderRole, selection: Provider | null) => {
      emitProviders(
        setSingleAssignment(entryProviders, role, selection ? toAssignment(selection, role) : null),
      );
    },
    [entryProviders, emitProviders],
  );

  /** Appends one provider to a multi-assignment role, preserving insertion order (FR-008). */
  const addToRole = useCallback(
    (role: ProviderRole, selection: Provider) => {
      emitProviders([...entryProviders, toAssignment(selection, role)]);
    },
    [entryProviders, emitProviders],
  );

  /** Replaces the provider in one row of a multi-assignment role, in place (FR-011 order). */
  const replaceInRole = useCallback(
    (role: ProviderRole, providerId: string, selection: Provider) => {
      emitProviders(
        entryProviders.map((a) =>
          a.role === role && a.providerId === providerId ? toAssignment(selection, role) : a,
        ),
      );
    },
    [entryProviders, emitProviders],
  );

  /** Removes one assignment without disturbing its siblings (FR-010). */
  const removeAssignment = useCallback(
    (role: ProviderRole, providerId: string) => {
      emitProviders(entryProviders.filter((a) => !(a.role === role && a.providerId === providerId)));
    },
    [entryProviders, emitProviders],
  );

  const billingModes = getBillingModes(claimType);

  // Mode is data-driven whenever a surgeon/co-surgeon is already assigned (keeps
  // edit-load and the clear-on-switch invariant consistent); when only a shared
  // role (Physicians Assistant) or nothing is set, the manual toggle decides.
  const derivedMode: BillingMode | null = entryProviders.some(
    (a) => a.role === 'CO_SURGEON_1' || a.role === 'CO_SURGEON_2',
  )
    ? 'cosurgery'
    : entryProviders.some((a) => a.role === 'PRIMARY_SURGEON' || a.role === 'ASSISTANT_SURGEON')
      ? 'standard'
      : null;
  const [manualMode, setManualMode] = useState<BillingMode>('standard');
  const billingMode: BillingMode = derivedMode ?? manualMode;

  // How many EMPTY picker rows a multi-assignment role is showing, keyed by role code. An
  // unfilled row has no assignment to live in, so it is UI state — the same role
  // useFieldArray plays for the CPT/ASA code rows this pattern mirrors.
  //
  // Stored as an absolute count, not a delta, and always written from the role's current
  // effective count (see `emptyRows` below, which floors it at one row while the role is
  // unassigned so its slot is never blank). Deriving the count and then incrementing the
  // raw stored value is what made the first "Add" click a no-op.
  //
  // Filling a row decrements it: unlike a code row, which exists in the field array both
  // before and after a value is typed into it, an empty picker row is REPLACED by the
  // assignment row it produces, so leaving the count alone spawns a fresh empty row.
  const [emptyRowsByRole, setEmptyRowsByRole] = useState<Partial<Record<ProviderRole, number>>>({});
  const setEmptyRows = useCallback((role: ProviderRole, next: number) => {
    setEmptyRowsByRole((prev) => ({ ...prev, [role]: Math.max(0, next) }));
  }, []);

  const handleBillingModeChange = useCallback(
    (next: BillingMode) => {
      setManualMode(next);
      // Switching clears the other mode's surgeon picks; Physicians Assistant is shared.
      const drop: ProviderRole[] =
        next === 'cosurgery'
          ? ['PRIMARY_SURGEON', 'ASSISTANT_SURGEON']
          : ['CO_SURGEON_1', 'CO_SURGEON_2'];
      emitProviders(entryProviders.filter((a) => !drop.includes(a.role)));
    },
    [entryProviders, emitProviders],
  );

  // Ids already spoken for on a multi-assignment role. A provider may hold only one of
  // this claim's multi-assignment roles (FR-009); single-assignment roles (surgical) keep
  // their own narrower dedupe rules below.
  const multiAssignRoles = new Set(roles.filter((r) => r.multiple).map((r) => r.code));
  const idsOnMultiRoles = new Set(
    entryProviders.filter((a) => multiAssignRoles.has(a.role)).map((a) => a.providerId),
  );

  if (roles.length === 0) {
    return null;
  }

  /** Scaffold shared by both the SURGICAL billing-mode branch and the default branch. */
  function Panel({ skeletonCount, children }: { skeletonCount: number; children: ReactNode }) {
    return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Required data unavailable. Please try again later.
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {Array.from({ length: skeletonCount }, (_, i) => (
              <Skeleton key={i} variant="rounded" height={120} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {children}
          </Box>
        )}
        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}
      </Box>
    );
  }

  /** Locates a suggestion's source region in the document viewer, if it has one. */
  const locateHandler = (suggestion: ClaimProviderSuggestion, roleLabel: string) =>
    suggestion.source && onLocate
      ? () =>
          onLocate(
            suggestion.source!.documentId,
            suggestion.source!.page,
            suggestion.source!.bbox,
            roleLabel,
          )
      : undefined;

  /**
   * Renders one role's input. A single-assignment role shows a chip once filled and a
   * picker until then; a multi-assignment role shows repeatable rows with an "Add" button,
   * mirroring the CPT / ASA code lists (feature 044, FR-008).
   *
   * Options are the providers eligible for the role by credential (FR-002), minus those
   * already spoken for: a multi-assignment role excludes anyone on either of this claim's
   * multi-assignment roles (FR-009), while a single-assignment role excludes anyone
   * holding a *different* role on the claim — one person, one role per claim (the
   * pre-existing surgical rule, which the schema also enforces for state that does not
   * arrive through these inputs).
   */
  const renderProviderInput = (roleDef: RoleDefinition) => {
    const role = roleDef.code;
    const roleAssignments = entryProviders.filter((a) => a.role === role);
    const eligible = providers.filter((p) => isEligibleForRole(p, roleDef));
    const options = roleDef.multiple
      ? eligible.filter((p) => !idsOnMultiRoles.has(p.id))
      : eligible.filter((p) => !entryProviders.some((a) => a.role !== role && a.providerId === p.id));

    const picker = (
      <Autocomplete
        fullWidth
        options={options}
        getOptionLabel={(p) => p.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        size="small"
        disabled={locked || !!error}
        clearOnBlur
        selectOnFocus
        onChange={(_e, selected) => {
          if (selected) updateRole(role, selected);
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={`Select ${roleDef.label}`} size="small" />
        )}
      />
    );

    // The role's slot is never hidden, so say why it is empty rather than showing a
    // picker with nothing in it (spec Edge Cases: a practice with no CRNAs).
    const emptyEligible = (
      <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
        No eligible {roleDef.label} providers in this practice.
      </Typography>
    );

    // A multi-assignment role reads as repeatable rows with an "Add" button beneath —
    // the same shape the CPT and ASA code lists use (CodeRows), so adding a second
    // anesthesiologist feels like adding a second CPT code. One row per assignment, each
    // editable in place with a remove control, plus any empty rows the biller has opened.
    if (roleDef.multiple) {
      const pending = (suggestions ?? []).filter(
        (s) => s.role === role && !idsOnMultiRoles.has(s.providerId),
      );
      // Floored at one row while the role is unassigned, so its slot is never blank (FR-004).
      const emptyRows = Math.max(emptyRowsByRole[role] ?? 0, roleAssignments.length === 0 ? 1 : 0);

      if (eligible.length === 0) return emptyEligible;

      /**
       * The remove control lives INSIDE the input, in MUI's clear-indicator slot ahead of the
       * dropdown arrow — the same place the provider Chip carries its delete icon, so a row
       * removes exactly where a chip does. `CancelIcon` for the same reason: every other
       * provider field in the form uses the Chip's default delete glyph, and matching the
       * provider vocabulary was chosen over matching the CPT/ASA rows this layout borrows.
       *
       * A filled row is always removable — clearing it falls back to the baseline empty row,
       * so the slot stays visible (FR-004). A lone EMPTY row is not: removing it would blank
       * the slot with nothing to fall back to, so it carries no control at all rather than an
       * inert one.
       */
      const removeAdornment = (index: number, onRemove: () => void, removable: boolean) =>
        removable ? (
          <IconButton
            aria-label={`Remove ${roleDef.label} row ${index + 1}`}
            onClick={onRemove}
            disabled={locked}
            size="small"
            edge="end"
            sx={{ color: 'text.secondary', mr: -0.5 }}
          >
            <CancelIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null;

      return (
        <Box>
          {roleAssignments.map((a, index) => {
            // The assigned provider is filtered out of `options` (already taken), so it is
            // added back for its own row or MUI treats the value as out-of-range. Falling
            // back to the assignment itself keeps a provider who has since left the
            // directory displayed as recorded (FR-024) rather than blanking the row.
            const assigned: Provider =
              providers.find((p) => p.id === a.providerId) ??
              { id: a.providerId, name: a.providerName, credential: a.credential, npi: a.npi };
            return (
              <Box key={a.providerId} sx={{ mb: 1 }}>
                <Autocomplete
                  fullWidth
                  options={[assigned, ...options]}
                  value={assigned}
                  getOptionLabel={(p) => p.name}
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  size="small"
                  disabled={locked || !!error}
                  // Suppresses MUI's own clear indicator so the row shows one remove
                  // control, not two — removal is the adornment below.
                  disableClearable
                  onChange={(_e, selected) => {
                    if (selected) replaceInRole(role, a.providerId, selected);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      slotProps={{
                        htmlInput: { ...params.inputProps, 'aria-label': `${roleDef.label} ${index + 1}` },
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {removeAdornment(index, () => removeAssignment(role, a.providerId), true)}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Box>
            );
          })}

          {Array.from({ length: emptyRows }, (_, i) => {
            const index = roleAssignments.length + i;
            const removable = roleAssignments.length + emptyRows > 1;
            return (
              <Box key={`empty-${i}`} sx={{ mb: 1 }}>
                <Autocomplete
                  fullWidth
                  options={options}
                  value={null as Provider | null}
                  getOptionLabel={(p) => p.name}
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  size="small"
                  disabled={locked || !!error}
                  clearOnBlur
                  selectOnFocus
                  blurOnSelect
                  onChange={(_e, selected) => {
                    if (!selected) return;
                    addToRole(role, selected);
                    // This empty row has become an assignment row — consume it, or the next
                    // render adds a replacement empty row beneath it.
                    setEmptyRows(role, emptyRows - 1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={`Select ${roleDef.label}`}
                      size="small"
                      slotProps={{
                        htmlInput: { ...params.inputProps, 'aria-label': `${roleDef.label} ${index + 1}` },
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {removeAdornment(index, () => setEmptyRows(role, emptyRows - 1), removable)}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Box>
            );
          })}

          {pending.map((s) => (
            <Box key={s.providerId} sx={{ mb: 1 }}>
              <InlineAiSuggestion
                suggestion={s.providerName}
                currentValue=""
                onAccept={() => {
                  const provider = providers.find((p) => p.id === s.providerId);
                  if (provider) addToRole(role, provider);
                }}
                locked={locked}
                onLocate={locateHandler(s, roleDef.label)}
              />
            </Box>
          ))}

          <Button
            type="button"
            onClick={() => setEmptyRows(role, emptyRows + 1)}
            disabled={locked || !!error}
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
            sx={{ textTransform: 'none', fontSize: 13, color: 'primary.main', px: 0.5 }}
          >
            Add {roleDef.label}
          </Button>
        </Box>
      );
    }

    const roleAssignment = roleAssignments[0];
    if (roleAssignment) {
      return (
        <Chip
          label={roleAssignment.providerName}
          onDelete={locked ? undefined : () => updateRole(role, null)}
          variant="outlined"
          sx={chipStyles}
        />
      );
    }

    const suggestion = suggestions?.find((s) => s.role === role);

    return (
      <>
        {eligible.length === 0 ? emptyEligible : picker}
        {suggestion && (
          <InlineAiSuggestion
            suggestion={suggestion.providerName}
            currentValue=""
            onAccept={() => updateRole(role, providers.find((p) => p.id === suggestion.providerId) ?? null)}
            locked={locked}
            onLocate={locateHandler(suggestion, roleDef.label)}
          />
        )}
      </>
    );
  };

  // SURGICAL: a Standard / Co-surgery radio toggle drives which fields are shown.
  // Standard = Primary Surgeon, Assistant Surgeon, Physicians Assistant.
  // Co-surgery = Co-Surgeon 1, Co-Surgeon 2, Physicians Assistant.
  // A divider separates the Physicians Assistant from the surgeon fields.
  if (billingModes) {
    const activeRoles = billingModes[billingMode]
      .map((code) => roles.find((r) => r.code === code))
      .filter((r): r is RoleDefinition => !!r);

    return (
      <Panel skeletonCount={3}>
        <RadioGroup
          row
          value={billingMode}
          onChange={(e) => handleBillingModeChange(e.target.value as BillingMode)}
          sx={{ gap: 3 }}
        >
          <FormControlLabel
            value="standard"
            disabled={locked || !!error}
            control={<Radio size="small" />}
            label="Standard billing"
            slotProps={{ typography: { sx: { fontSize: 14, color: 'text.primary' } } }}
          />
          <FormControlLabel
            value="cosurgery"
            disabled={locked || !!error}
            control={<Radio size="small" />}
            label="Co-surgery billing"
            slotProps={{ typography: { sx: { fontSize: 14, color: 'text.primary' } } }}
          />
        </RadioGroup>

        {activeRoles.map((roleDef) => (
          <RoleTileShell key={roleDef.code} roleDef={roleDef} labelSx={labelSx}>
            <Box sx={{ width: { xs: '100%', sm: '50%', md: '100%' } }}>
              {renderProviderInput(roleDef)}
            </Box>
          </RoleTileShell>
        ))}
      </Panel>
    );
  }

  // Every configured role renders, stacked one above the other. No role is conditional on
  // what another holds — the anesthesia CRNA slot used to appear only once a CRNA had been
  // picked as the anesthesia provider, which is exactly what feature 044 removed (FR-004).
  return (
    <Panel skeletonCount={roles.length}>
      {roles.map((roleDef: RoleDefinition) => (
        <RoleTileShell key={roleDef.code} roleDef={roleDef} labelSx={labelSx}>
          <Box sx={{ width: { xs: '100%', sm: '50%', md: '100%' } }}>
            {renderProviderInput(roleDef)}
          </Box>
        </RoleTileShell>
      ))}
    </Panel>
  );
});

export default ClaimTypeProviderPanel;
